import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";

export const STATUS_COLORS = {
  attribue: "#7A4B2E",
  a_reaffecter: "#B45309",
  non_couvert: "#B91C1C",
  disponible: "#7C9885",
};
export const STATUS_LABELS_MAP = {
  attribue: "Attribué", a_reaffecter: "À réaffecter", non_couvert: "Non couvert", disponible: "Disponible",
};
export const STATUS_ORDER = ["non_couvert", "a_reaffecter", "attribue", "disponible"];

export const deriveStatus = (t) => {
  if (t.status === "attribue") return "attribue";
  if (t.status === "reserve") return "a_reaffecter";
  return (t.pending_uncovered_count || 0) > 0 ? "non_couvert" : "disponible";
};

const FitBounds = ({ geojson }) => {
  const map = useMap();
  useEffect(() => {
    if (!geojson.features.length) return;
    try {
      const layer = L.geoJSON(geojson);
      const bounds = layer.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    } catch {
      // ignore, garde le centrage par défaut
    }
  }, [geojson, map]);
  return null;
};

// Carte des territoires réels V4 (un secteur = un polygone officiel, plus de regroupement départemental)
// Les géométries (lourdes) sont chargées à part, indépendamment de la liste/KPI, pour ne jamais bloquer la page.
export const AdminTerritoriesMap = ({ territories, onOpenTerritory, geometriesUrl = "/admin/territories/geometries", fitBounds = false, height = "h-[600px]" }) => {
  const [geometries, setGeometries] = useState(null);

  useEffect(() => {
    api.get(geometriesUrl).then((res) => {
      setGeometries(Object.fromEntries(res.data.map((t) => [t.id, t.geometry])));
    }).catch(() => setGeometries({}));
  }, [geometriesUrl]);

  const byId = useMemo(() => Object.fromEntries(territories.map((t) => [t.id, t])), [territories]);

  const geojson = useMemo(() => ({
    type: "FeatureCollection",
    features: territories.filter((t) => geometries?.[t.id]).map((t) => ({
      type: "Feature", geometry: geometries[t.id], properties: { id: t.id },
    })),
  }), [territories, geometries]);

  const styleFor = (id) => {
    const t = byId[id];
    const derived = t ? deriveStatus(t) : "disponible";
    return { fillColor: STATUS_COLORS[derived], fillOpacity: 0.62, color: "#ffffff", weight: 1 };
  };

  const onEachFeature = (feature, layer) => {
    const t = byId[feature.properties.id];
    if (!t) return;
    layer.on({
      mouseover: (e) => e.target.setStyle({ fillOpacity: 0.92 }),
      mouseout: (e) => e.target.setStyle(styleFor(t.id)),
      click: () => onOpenTerritory(t),
    });
    layer.bindTooltip(`<strong>${t.code} — ${t.name}</strong><br/>${STATUS_LABELS_MAP[deriveStatus(t)]}`, { sticky: true });
  };

  return (
    <div data-testid="territories-map-wrapper" className="relative">
      <div className="mb-3 flex flex-wrap gap-4 text-xs text-slate-500">
        {STATUS_ORDER.map((k) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLORS[k] }} />
            {STATUS_LABELS_MAP[k]}
          </span>
        ))}
      </div>
      <div data-testid="territories-map" className={`${height} w-full overflow-hidden rounded-2xl border border-slate-200`}>
        {geojson.features.length > 0 ? (
          <MapContainer center={[46.6, 2.2]} zoom={6} minZoom={5} maxZoom={9} preferCanvas style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            <GeoJSON key={geojson.features.length} data={geojson} style={(feature) => styleFor(feature.properties.id)} onEachFeature={onEachFeature} />
            {fitBounds && <FitBounds geojson={geojson} />}
          </MapContainer>
        ) : (
          <div data-testid="territories-map-loading" className="flex h-full items-center justify-center text-sm text-slate-400">
            {geometries === null ? "Chargement de la carte…" : "Aucune géométrie à afficher"}
          </div>
        )}
      </div>
    </div>
  );
};
